'use client';

import { useMemo } from 'react';

import type { FieldingGroup } from '@highlight-cp/project-api';

import { computeDaysToCompleteFielding } from './compute-days-to-complete-fielding';

interface UseDaysToCompleteFieldingProps {
  /**
   * Fielding groups whose step durations are used to compute total fielding time.
   * When unset/empty, the hook returns `0`.
   */
  fieldingGroups?: FieldingGroup[] | null;
}

/**
 * ### useDaysToCompleteFielding
 *
 * @description
 * Computes the number of days required to complete fielding from the configured fielding
 * groups. For each fielding group, it sums step durations (treating missing durations as `0`).
 * Group steps use the max of their `groupedSteps` durations (via `getStepDuration`). Returns the
 * maximum total across all fielding groups.
 *
 * @param props - Hook parameters
 * @param props.fieldingGroups - Fielding groups including step durations
 * @returns Non-negative number of days to complete fielding.
 */
const useDaysToCompleteFielding = ({ fieldingGroups }: UseDaysToCompleteFieldingProps) => {
  const daysToCompleteFielding = useMemo(
    () => computeDaysToCompleteFielding(fieldingGroups),
    [fieldingGroups],
  );

  return daysToCompleteFielding;
};

export default useDaysToCompleteFielding;
